import { useEffect, useId, useRef, useState } from "react";

interface CheckboxOption {
  id: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

export function CheckboxMultiSelect({
  legend,
  value,
  options,
  onChange,
  placeholder = "请选择（可多选）"
}: {
  legend: string;
  value: string[];
  options: CheckboxOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}): React.JSX.Element {
  const popupId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.filter((option) => value.includes(option.id));
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) =>
        option.label.toLocaleLowerCase().includes(normalizedQuery)
      )
    : options;
  const enabledIds = options
    .filter((option) => !option.disabled || value.includes(option.id))
    .map((option) => option.id);
  const allEnabledSelected =
    enabledIds.length > 0 && enabledIds.every((id) => value.includes(id));

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  function toggle(id: string, checked: boolean): void {
    onChange(
      checked
        ? [...value, id].filter((item, index, items) => items.indexOf(item) === index)
        : value.filter((item) => item !== id)
    );
  }

  function close(): void {
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  }

  function toggleOpen(): void {
    if (open) setQuery("");
    setOpen(!open);
  }

  function handleControlKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (!open || event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    close();
  }

  const selectedSummary = selected.length <= 2
    ? selected.map((option) => option.label).join("、")
    : `${selected.slice(0, 2).map((option) => option.label).join("、")}等 ${selected.length} 项`;

  return (
    <fieldset className="assistant-multi-select generic-multi-select">
      <legend>{legend}</legend>
      <div
        className="multi-select-control"
        ref={rootRef}
        onKeyDown={handleControlKeyDown}
      >
        <button
          ref={triggerRef}
          className="multi-select-trigger"
          type="button"
          aria-expanded={open}
          aria-controls={popupId}
          aria-label={`${legend}，已选 ${selected.length} 项`}
          onClick={toggleOpen}
        >
          <span className={selected.length ? "" : "placeholder"}>
            {selected.length ? selectedSummary : placeholder}
          </span>
          <small>已选 {selected.length} 项</small>
          <span className="multi-select-chevron" aria-hidden="true">⌄</span>
        </button>
        {open ? (
          <div
            id={popupId}
            className="multi-select-popover"
            aria-label={`${legend}多选列表`}
          >
            {options.length > 7 ? (
              <div className="multi-select-search">
                <input
                  type="search"
                  value={query}
                  aria-label={`搜索${legend}`}
                  placeholder={`搜索${legend}`}
                  autoFocus
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
            ) : null}
            <div className="multi-select-toolbar">
              <button
                className="text-button"
                type="button"
                disabled={allEnabledSelected}
                onClick={() => onChange(enabledIds)}
              >
                全选
              </button>
              <button
                className="text-button"
                type="button"
                disabled={selected.length === 0}
                onClick={() => onChange([])}
              >
                清空
              </button>
            </div>
            <div className="multi-select-options">
              {visibleOptions.length ? (
                visibleOptions.map((option) => {
                  const checked = value.includes(option.id);
                  return (
                    <label className="check-row" key={option.id}>
                      <input
                        checked={checked}
                        disabled={option.disabled && !checked}
                        type="checkbox"
                        onChange={(event) => toggle(option.id, event.target.checked)}
                      />
                      <span>
                        {option.label}
                        {option.disabled && option.disabledReason ? (
                          <small>{option.disabledReason}</small>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              ) : (
                <p className="multi-select-empty">
                  {options.length ? "没有匹配项" : "暂无可选项"}
                </p>
              )}
            </div>
            <div className="multi-select-footer">
              <span>已选 {selected.length} 项</span>
              <button className="secondary-button" type="button" onClick={close}>
                完成
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
